import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '../App';
import { Globe, MessageSquare, Search, Mail, Save, ExternalLink } from 'lucide-react';

const FIELDS = [
  {
    section: 'Ana Sayfa İçeriği',
    icon: Globe,
    color: '#1d4ed8',
    items: [
      { key: 'hero_baslik', label: 'Ana Başlık', type: 'text', hint: 'Ziyaretçilerin ilk gördüğü büyük başlık' },
      { key: 'hero_aciklama', label: 'Alt Açıklama', type: 'textarea', hint: 'Hero bölümünün altındaki açıklama metni' },
    ],
  },
  {
    section: 'İletişim & WhatsApp',
    icon: MessageSquare,
    color: '#16a34a',
    items: [
      { key: 'whatsapp_no', label: 'WhatsApp Numarası', type: 'text', hint: 'Uluslararası formatta örn: 905001234567 (+ olmadan)' },
      { key: 'demo_mesaj', label: 'Demo Talep Mesajı', type: 'textarea', hint: 'WhatsApp demo butonuna basıldığında gönderilecek ön mesaj' },
      { key: 'iletisim_email', label: 'İletişim E-postası', type: 'email', hint: 'Footer ve iletişim bölümünde gösterilir' },
    ],
  },
  {
    section: 'SEO Ayarları',
    icon: Search,
    color: '#7c3aed',
    items: [
      { key: 'seo_baslik', label: 'Sayfa Başlığı (Title)', type: 'text', hint: 'Tarayıcı sekmesinde ve arama sonuçlarında görünür (60 karakter önerilir)' },
      { key: 'seo_aciklama', label: 'Meta Açıklama', type: 'textarea', hint: 'Google arama sonuçlarında görünen açıklama (160 karakter önerilir)' },
      { key: 'seo_keywords', label: 'Anahtar Kelimeler', type: 'text', hint: 'Virgülle ayrılmış anahtar kelimeler' },
    ],
  },
];

export default function SiteAyarlari() {
  const [form, setForm] = useState({
    hero_baslik: '', hero_aciklama: '',
    whatsapp_no: '', demo_mesaj: '', iletisim_email: '',
    seo_baslik: '', seo_aciklama: '', seo_keywords: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/site-ayarlari').then(({ data }) => {
      setForm(f => ({ ...f, ...data }));
    }).catch(() => toast.error('Ayarlar yüklenemedi')).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/site-ayarlari', form);
      toast.success('Ayarlar kaydedildi');
    } catch {
      toast.error('Kaydetme başarısız');
    } finally {
      setSaving(false);
    }
  };

  const wpUrl = form.whatsapp_no
    ? `https://wa.me/${form.whatsapp_no}?text=${encodeURIComponent(form.demo_mesaj || 'Demo talep ediyorum')}`
    : null;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
      <div className="spinner" style={{ width: 28, height: 28 }} />
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Site Ayarları</h1>
          <p className="page-subtitle">Ana sayfa içeriği, iletişim ve SEO ayarları</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a href="/" target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--bg-card)', color: 'var(--text-secondary)', textDecoration: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
            <ExternalLink size={14} /> Siteyi Görüntüle
          </a>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ gap: 8 }}>
            {saving ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <Save size={14} />}
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {FIELDS.map(section => (
          <div key={section.section} className="card" style={{ padding: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, paddingBottom: 20, borderBottom: '1px solid var(--border)' }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: `${section.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <section.icon size={20} color={section.color} />
              </div>
              <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>{section.section}</h2>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {section.items.map(item => (
                <div key={item.key}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
                    {item.label}
                  </label>
                  {item.type === 'textarea' ? (
                    <textarea
                      value={form[item.key] || ''}
                      onChange={e => setForm(f => ({ ...f, [item.key]: e.target.value }))}
                      rows={3}
                      style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--bg-input)', resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }}
                      onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'}
                    />
                  ) : (
                    <input
                      type={item.type || 'text'}
                      value={form[item.key] || ''}
                      onChange={e => setForm(f => ({ ...f, [item.key]: e.target.value }))}
                      style={{ width: '100%', padding: '10px 14px', border: '1.5px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'inherit', color: 'var(--text-primary)', background: 'var(--bg-input)', outline: 'none', boxSizing: 'border-box' }}
                      onFocus={e => e.target.style.borderColor = 'var(--blue)'}
                      onBlur={e => e.target.style.borderColor = 'var(--border)'}
                    />
                  )}
                  {item.hint && (
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>{item.hint}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* WhatsApp preview */}
        {wpUrl && (
          <div className="card" style={{ padding: 24 }}>
            <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 16px', color: 'var(--text-primary)' }}>WhatsApp Önizleme</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', background: '#f0fdf4', borderRadius: 14, border: '1.5px solid #bbf7d0' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#25D366', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#166534', marginBottom: 2 }}>wa.me/{form.whatsapp_no}</div>
                <div style={{ fontSize: 12, color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.demo_mesaj}</div>
              </div>
              <a href={wpUrl} target="_blank" rel="noreferrer"
                style={{ padding: '8px 16px', background: '#25D366', color: '#fff', textDecoration: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
                Test Et
              </a>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 8 }}>
          <button className="btn btn-primary" onClick={save} disabled={saving} style={{ gap: 8, padding: '11px 28px' }}>
            {saving ? <div className="spinner" style={{ width: 14, height: 14 }} /> : <Save size={15} />}
            {saving ? 'Kaydediliyor...' : 'Tüm Ayarları Kaydet'}
          </button>
        </div>
      </div>
    </div>
  );
}
